export type CoverFocusPoint = {
  x: number;
  y: number;
};

export type CoverImageMeta = {
  focusPoint?: CoverFocusPoint;
  variants?: Record<string, string>;
  aspectRatio?: string;
  updatedAt?: string;
};

const ALLOWED_VARIANT_KEYS = new Set(["1600", "800", "400", "1600x900", "800x450", "400x225"]);

function isHttpUrl(input: unknown) {
  if (typeof input !== "string" || !input.trim()) return false;
  try {
    const url = new URL(input);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function clampPercent(input: unknown) {
  const value = Number(input);
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function normalizeCoverImageMeta(input: unknown): CoverImageMeta | null {
  if (typeof input !== "object" || input === null) return null;
  const raw = input as Record<string, unknown>;
  const meta: CoverImageMeta = {};

  if (typeof raw.focusPoint === "object" && raw.focusPoint !== null) {
    const focus = raw.focusPoint as Record<string, unknown>;
    const x = clampPercent(focus.x);
    const y = clampPercent(focus.y);
    if (x !== null && y !== null) meta.focusPoint = { x, y };
  }

  if (typeof raw.variants === "object" && raw.variants !== null) {
    const variants: Record<string, string> = {};
    for (const [key, url] of Object.entries(raw.variants as Record<string, unknown>)) {
      if (ALLOWED_VARIANT_KEYS.has(key) && typeof url === "string" && isHttpUrl(url)) variants[key] = url;
    }
    if (Object.keys(variants).length) meta.variants = variants;
  }

  if (raw.aspectRatio === "16:9") meta.aspectRatio = "16:9";

  if (typeof raw.updatedAt === "string") {
    const parsed = new Date(raw.updatedAt);
    if (!Number.isNaN(parsed.getTime())) meta.updatedAt = parsed.toISOString();
  }

  return Object.keys(meta).length ? meta : null;
}

export function parseCoverImageMetaJson(input: string) {
  if (!input.trim()) return null;
  try {
    return normalizeCoverImageMeta(JSON.parse(input));
  } catch {
    return null;
  }
}

export function coverImageObjectPosition(meta: unknown) {
  const focusPoint = normalizeCoverImageMeta(meta)?.focusPoint;
  return focusPoint ? `${focusPoint.x}% ${focusPoint.y}%` : "50% 50%";
}

export function coverImageVariant(meta: unknown, preferred: "1600" | "800" | "400") {
  const variants = normalizeCoverImageMeta(meta)?.variants;
  if (!variants) return null;
  if (variants[preferred]) return variants[preferred];
  const legacyKey = preferred === "1600" ? "1600x900" : preferred === "800" ? "800x450" : "400x225";
  return variants[legacyKey] ?? null;
}

export function coverImageSrc(fallbackUrl: string | null | undefined, meta: unknown, preferred: "1600" | "800" | "400") {
  return coverImageVariant(meta, preferred) ?? fallbackUrl ?? "";
}
